import { MenuDrink } from "./menu.drink.model";
import { MenuDrinkBuilder } from "./menu.drink.builder";
import {
  DocumentReference,
  DocumentData,
  QueryDocumentSnapshot,
  AngularFirestore,
  CollectionReference,
  Query
} from "@angular/fire/firestore";
import { environment } from "src/environments/environment";
import { MenuCategory } from "../category/menu.category.model";
import { BehaviorSubject, Observable, of } from "rxjs";
import { catchError, map, switchMap, take } from "rxjs/operators";
import { Order, MenuOrderData } from "../../order/order.model";
import { ProductType } from "../../product/enums";
import { TimeframeFilter, MonthToDateFilter } from "../../timeframe/timeframes";
import { MenuOrderExporter } from "./menu.drink.export";
import { MenuSalesAnalytics } from "./menu.drink.analytics";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { ToastService } from "ng-uikit-pro-standard";
import { DrinkPosition } from "./position/drink.position.model";
import { DrinkPositionBuilder } from "./position/drink.position.builder";
import { AuthService } from "../../services";

export class MenuDrinkFactory {
  public drinks$: BehaviorSubject<MenuDrink[]>;
  public drinkOrders$: Observable<MenuOrderData[]>;
  public analytics: MenuSalesAnalytics;

  public query: DrinkOrderQuery;

  public builder: MenuDrinkBuilder;

  constructor(
    private venueId: string,
    private _reference: DocumentReference,
    private db: AngularFirestore,
    private toast: ToastService,
    private httpClient: HttpClient,
    private authService: AuthService,
  ) {
    this.drinks$ = new BehaviorSubject([]);

    this._start();
  }

  public _start() {
    this.analytics = new MenuSalesAnalytics();
    this.query = new DrinkOrderQuery(this._reference);

    this._refresh();
  }
  public _refresh() {
    this._refreshDrinks();
    this._refreshOrders();
  }

  public async _refreshDrinks() {
    const userJWT = await this.authService.user.getIdToken();
    
    this.httpClient.get<MenuCategory[]>(`${environment.apiBaseUrl}/api/v1/drinks/${this.venueId}`, {
      headers: new HttpHeaders().set("Authorization", `${userJWT}`),
    }).subscribe((drinksResponse) => {
      const drinks = drinksResponse
        .map(drinksResponse => new MenuDrinkBuilder().loadAPIResponse(drinksResponse, this.db).build())

      this.drinks$.next(drinks);
    })
  }

  public _refreshOrders() {
    this.drinkOrders$ = this.db
      .collection(environment.orders, ref => this.query.query(ref))
      .snapshotChanges()
      .pipe<MenuOrderData[]>(
        map(orders => {
          this.analytics.initialize();
          this.analytics.setTimeframe(this.query.timeframe);

          let x: MenuOrderData[] = [];
          for (let i = 0; i < orders.length; i++) {
            let orderData: MenuOrderData[] = this._toMenuOrder(
              orders[i].payload.doc
            );

            this.analytics.addOrder(
              new Order(orders[i].payload.doc.data(), orders[i].payload.doc.ref)
            );
            x = x.concat(orderData);
          }
          this.analytics.next();
          this.query.totalDrinks = x.length;
          return x;
        })
      );
  }

  public create = () => (this.builder = new MenuDrinkBuilder());

  public remove = (drink: MenuDrink) => this._delete(drink.reference.id);

  public toggleIsActive = async (drink: MenuDrink) => {
    this.fix({
      ...drink,
      isActive: !drink.isActive,
    } as MenuDrink);
    
    await this._update();
  }
  public fix = (drink: MenuDrink) =>
    (this.builder = new MenuDrinkBuilder().edit(drink));

  public async deploy() {
    this.builder.hasReference ? await this._update() : await this._add();
  }

  private async _add() {
    const currentDrinks = this.drinks$.getValue();
    const userJWT = await this.authService.user.getIdToken();

    await this.drinks$.pipe(
      take(1),
      switchMap(drinks => this.httpClient.post(`${environment.apiBaseUrl}/api/v1/drinks`, {
        venueId: this.venueId,
        data: this.builder.toAPIResponse(),
      }, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
      }).pipe(
        map((createDrinkResponse) => {
          return [...drinks, new MenuDrinkBuilder().loadAPIResponse(createDrinkResponse, this.db).build()];
        }),
        catchError((err) => {
          this.toast.error(`Could not create category: ${this.builder.name}`, "Error", {
            opacity: 1,
          });
          return of(currentDrinks);
        })
      ))
    ).subscribe(drinks => this.drinks$.next(drinks));
  }
  private async _update() {
    const currentDrinks = this.drinks$.getValue();
    const userJWT = await this.authService.user.getIdToken();

    await this.drinks$.pipe(
      take(1),
      switchMap(drinks => this.httpClient.put(`${environment.apiBaseUrl}/api/v1/drinks`, {
        id: this.builder.id,
        venueId: this.venueId,
        data: this.builder.toAPIResponse(),
      }, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
      }).pipe(
        map((updatedDrinkResponse) => {
          const updatedDrink = new MenuDrinkBuilder().loadAPIResponse(updatedDrinkResponse, this.db).build();
          return drinks.map(drink => {
            return drink.id === updatedDrink.id ?
              updatedDrink :
              drink;
          })
        }),
        catchError((err) => {
          this.toast.error(`Could not update drink: ${this.builder.name}`, "Error", {
            opacity: 1,
          });
          return of(currentDrinks);
        })
      ))
    ).subscribe(drinks => this.drinks$.next(drinks));
  }

  private _toMenuOrder(
    doc: QueryDocumentSnapshot<DocumentData>
  ): MenuOrderData[] {
    return new Order(doc.data(), doc.ref).mapOrderToMenu();
  }

  private async _delete(id: string) {
    // get the currentDrinks to keep the current state in the event of an error.
    const currentDrinks = this.drinks$.getValue();
    const userJWT = await this.authService.user.getIdToken();

    this._clean();
    await this.drinks$.pipe(
      take(1),
      switchMap(drinks => this.httpClient.delete(`${environment.apiBaseUrl}/api/v1/drinks/${this.venueId}/${id}`, {
        headers: new HttpHeaders().set("Authorization", `${userJWT}`),
      }).pipe(
        map(() => {
          return drinks.filter(drink => drink.id !== id)
        }),
        catchError((err) => {
          this.toast.error("Could not delete category.", "Error", {
            opacity: 1,
          });
          return of(currentDrinks);
        })
      ))
    ).subscribe(drinks => this.drinks$.next(drinks));
  }

  private _clean = () => (this.builder = null);

  public copy = (drink: MenuDrink) =>
    (this.builder = new MenuDrinkBuilder().duplicate(drink));

  public async reposition(drinks: MenuDrink[], category: MenuCategory) {
    for (let i = 0; i < drinks.length; i++) {
      this.updatePosition(drinks[i], category, i);
    }
  }

  private updatePosition = async (drink: MenuDrink, category: MenuCategory, updatedPosition: number) => {
    let newCategories: DrinkPosition[] = [];

    this.fix({
      ...drink,
    } as MenuDrink);

    for (let i = 0; i < this.builder.categories.length; i++) {
      if (this.builder.categories[i].categoryId == category.id) {
        newCategories.push({ categoryId: category.id, position: updatedPosition });
      } else {
        newCategories.push(new DrinkPositionBuilder().edit(this.builder.categories[i]).toJSON());
      }
    }

    this.fix({
      ...drink,
      categories: newCategories,
    } as MenuDrink);
    
    await this._update();
  }

  public onMenuOrderFilterTimeframeSelected(tf: TimeframeFilter) {
    this.query.setTimeframe(tf);
    this.analytics.setTimeframe(tf);
    this._refreshOrders();
  }
  public onMenuOrderFilterCategorySelected(category: MenuCategory) {
    this.query.setCategory(category);
  }
  public onMenuOrderFilterDrinkSelected(drink: MenuDrink) {
    this.query.setDrink(drink);
  }
  public onMenuOrderFilterSearchChanged(text: string) {
    this.query.searchText = text;
  }
  public onMenuOrderFilterRefreshed() {
    this.query = new DrinkOrderQuery(this._reference);
  }
  public get menuOrderFilterSelectedDrink(): MenuDrink {
    return this.query.drink;
  }
  public get menuOrderFilterSelectedCategory(): MenuCategory {
    return this.query.category;
  }
  public get menuOrderFilterSearchText(): string {
    return this.query.searchText;
  }
  public get menuOrderFilterSelectedTimeframe(): TimeframeFilter {
    return this.query.timeframe;
  }
  public onMenuOrderExportPressed(orders: MenuOrderData[], isAdmin: boolean) {
    
    new MenuOrderExporter(orders, this.menuOrderFilterSelectedTimeframe).export(
      isAdmin
    );
  }
}

export class DrinkOrderQuery {
  private collection: CollectionReference;
  public category: MenuCategory;
  public timeframe: TimeframeFilter;
  public drink: MenuDrink;
  public searchText: string;
  public totalDrinks: number;

  constructor(private venue: DocumentReference) {
    this._default();
  }
  _default() {
    this.timeframe = new MonthToDateFilter();
    this.totalDrinks = 0;
  }

  setTimeframe(tf: TimeframeFilter) {
    this.timeframe = tf;
  }

  setDrink(drink: MenuDrink) {
    this.drink = drink;
  }
  setText(text: string) {
    this.searchText = text;
  }

  setCategory(category: MenuCategory) {
    this.category = category;
  }

  private qTimeframe(): Query {
    return this.collection
      .where("summary.purchasedAt", ">=", this.timeframe.begin)
      .where("summary.purchasedAt", "<=", this.timeframe.end);
  }

  private qPurchasedAt(query: Query): Query {
    return query.orderBy("summary.purchasedAt", "desc");
  }
  private qProduct(query: Query): Query {
    return query.where("product.type", "==", ProductType.Menu.valueOf());
  }
  private qVenue(query: Query): Query {
    return query.where("venue.reference", "==", this.venue);
  }
  public query(ref: CollectionReference): Query {
    this.collection = ref;
    return this.qPurchasedAt(this.qProduct(this.qVenue(this.qTimeframe())));
  }
}
